import { useEffect } from "react";

/**
 * Renders one or more JSON-LD objects as `<script type="application/ld+json">` tags in the
 * real document <head>.
 *
 * Not built on react-helmet-async (the project's mechanism for <title>/<meta>/<link> --
 * see Seo.jsx): under React 19 (this project's React version), react-helmet-async v3's
 * React19Dispatcher only gets automatic <head> hoisting from React itself for <title>,
 * <meta> and <link> -- that is a React 19 built-in behavior, not something react-helmet-async
 * implements for <script>. An inline (no `src`) <script>, like our JSON-LD payload, is not one
 * of React 19's auto-hoisted tag types, so it renders in place in the React tree instead of
 * <head>, confirmed via a direct DOM inspection in this environment's test run. Managing the
 * tag with a plain effect against document.head, the same approach react-helmet(-async) itself
 * uses internally for real DOM commits, sidesteps that gap entirely and is guaranteed correct
 * regardless of React/react-helmet-async version behavior.
 *
 * Prerender safety: every tag carries data-rc-jsonld="<key>" (a hash of its content). Before appending,
 * any existing tag with the same key (for example one baked into the prerendered HTML) is removed, so the
 * client never produces a second copy of the same graph.
 */
function keyOf(text) {
  let h = 5381;
  for (let i = 0; i < text.length; i += 1) h = ((h * 33) ^ text.charCodeAt(i)) >>> 0;
  return h.toString(36);
}

export default function JsonLd({ data }) {
  const items = data ? (Array.isArray(data) ? data : [data]) : [];
  const json = items.map((item) => JSON.stringify(item));

  useEffect(() => {
    if (json.length === 0) return undefined;
    const nodes = json.map((text) => {
      const key = keyOf(text);
      document.head
        .querySelectorAll(`script[type="application/ld+json"][data-rc-jsonld="${key}"]`)
        .forEach((old) => old.parentNode && old.parentNode.removeChild(old));
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.setAttribute("data-rc-jsonld", key);
      el.textContent = text;
      document.head.appendChild(el);
      return el;
    });
    return () => nodes.forEach((el) => el.parentNode?.removeChild(el));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [json.join("|")]);

  return null;
}
